import { z } from 'zod';
import { communicationsKnowledgeSignedPolicyCopyNotificationEventName, communicationsWorkforceProtectedFormNotificationEventName, } from './communications-system-notification-adapter.contract.js';
export { communicationsKnowledgeSignedPolicyCopyNotificationEventName, communicationsWorkforceProtectedFormNotificationEventName, };
// Consumer-owned duplicate of the minimized Documents wire contract. Communications
// imports no Documents code or tables, so either optional package remains removable.
export const communicationsDocumentNotificationIntentsCapability = 'communications.document-notification-intents.v1';
export const communicationsWorkforceProtectedFormNotificationPayloadSchema = z.strictObject({
    notificationKind: z.literal('workforce-protected-form-submitted'),
    subjectMembershipId: z.string().uuid(),
    directRecipientMembershipIds: z.tuple([z.string().uuid()]),
    recipientRoleKeys: z.tuple([z.literal('manager'), z.literal('owner')]),
    requestedChannels: z.tuple([z.literal('email'), z.literal('in-app')]),
    submissionId: z.string().uuid(),
    definitionId: z.string().uuid(),
    definitionVersionId: z.string().uuid(),
    definitionVersionNumber: z.number().int().positive(),
    submittedAt: z.string().datetime({ offset: true }),
    actionPath: z.literal('/documents/employee-forms'),
    protectedContentExcluded: z.literal(true),
    emailAttachmentsAllowed: z.literal(false),
}).superRefine((payload, context) => {
    if (payload.directRecipientMembershipIds[0] !== payload.subjectMembershipId) {
        context.addIssue({
            code: 'custom',
            message: 'The protected-form subject must be the direct notification recipient.',
            path: ['directRecipientMembershipIds', 0],
        });
    }
});
export const communicationsKnowledgeSignedPolicyCopyNotificationPayloadSchema = z.strictObject({
    notificationKind: z.literal('knowledge-signed-policy-copy'),
    subjectMembershipId: z.string().uuid(),
    directRecipientMembershipIds: z.tuple([z.string().uuid()]),
    recipientRoleKeys: z.tuple([]),
    requestedChannels: z.tuple([z.literal('email'), z.literal('in-app')]),
    artifactId: z.string().uuid(),
    documentId: z.string().uuid(),
    versionId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    title: z.string().trim().min(1).max(250),
    signedAt: z.string().datetime({ offset: true }),
    actionPath: z.literal('/documents/signed-policies'),
    copyMode: z.literal('secure-crest-readback'),
    signatureContentExcluded: z.literal(true),
}).superRefine((payload, context) => {
    if (payload.directRecipientMembershipIds[0] !== payload.subjectMembershipId) {
        context.addIssue({
            code: 'custom',
            message: 'The signed-policy subject must be the direct notification recipient.',
            path: ['directRecipientMembershipIds', 0],
        });
    }
});
export const communicationsDocumentNotificationPayloadSchema = z.discriminatedUnion('notificationKind', [
    communicationsWorkforceProtectedFormNotificationPayloadSchema,
    communicationsKnowledgeSignedPolicyCopyNotificationPayloadSchema,
]);
export const communicationsDocumentNotificationIntentSchema = z.strictObject({
    id: z.string().uuid(),
    sourceEventId: z.string().uuid(),
    sourceEventName: z.enum([
        communicationsWorkforceProtectedFormNotificationEventName,
        communicationsKnowledgeSignedPolicyCopyNotificationEventName,
    ]),
    sourceOccurredAt: z.string().datetime({ offset: true }),
    recipientMembershipId: z.string().uuid(),
    directRecipient: z.boolean(),
    recipientRoleKeys: z.array(z.enum(['manager', 'owner'])).max(2),
    subjectMembershipId: z.string().uuid(),
    subjectDisplayName: z.string().trim().min(1).max(200),
    notificationKind: z.enum([
        'workforce-protected-form-submitted',
        'knowledge-signed-policy-copy',
    ]),
    sourceResourceType: z.enum([
        'workforce-protected-form-submission',
        'knowledge-signed-policy-artifact',
    ]),
    sourceResourceId: z.string().uuid(),
    sourceDefinitionId: z.string().uuid().nullable(),
    sourceDocumentId: z.string().uuid().nullable(),
    sourceVersionId: z.string().uuid(),
    sourceVersionNumber: z.number().int().positive(),
    displayTitle: z.string().trim().min(1).max(250).nullable(),
    businessOccurredAt: z.string().datetime({ offset: true }),
    channel: z.enum(['email', 'in-app']),
    state: z.literal('pending'),
    actionPath: z.enum(['/documents/employee-forms', '/documents/signed-policies']),
    createdAt: z.string().datetime({ offset: true }),
}).superRefine((intent, context) => {
    if (!intent.directRecipient && intent.recipientRoleKeys.length === 0) {
        context.addIssue({
            code: 'custom',
            message: 'A document notification recipient must be direct or role-derived.',
            path: ['recipientRoleKeys'],
        });
    }
    const protectedForm = intent.notificationKind === 'workforce-protected-form-submitted';
    if (protectedForm && (intent.sourceEventName !== communicationsWorkforceProtectedFormNotificationEventName ||
        intent.sourceResourceType !== 'workforce-protected-form-submission' ||
        intent.sourceDefinitionId === null || intent.sourceDocumentId !== null ||
        intent.displayTitle !== null || intent.actionPath !== '/documents/employee-forms')) {
        context.addIssue({
            code: 'custom', message: 'The protected-form notification projection is inconsistent.',
        });
    }
    if (!protectedForm && (intent.sourceEventName !== communicationsKnowledgeSignedPolicyCopyNotificationEventName ||
        intent.sourceResourceType !== 'knowledge-signed-policy-artifact' ||
        intent.sourceDefinitionId !== null || intent.sourceDocumentId === null ||
        intent.displayTitle === null || intent.actionPath !== '/documents/signed-policies')) {
        context.addIssue({
            code: 'custom', message: 'The signed-policy notification projection is inconsistent.',
        });
    }
});
export const communicationsDocumentNotificationIntentQuerySchema = z.strictObject({
    scope: z.enum(['mine', 'all']).default('mine'),
    notificationKind: z.enum([
        'workforce-protected-form-submitted',
        'knowledge-signed-policy-copy',
    ]).nullable().default(null),
    channel: z.enum(['email', 'in-app']).nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
});
export const communicationsDocumentNotificationIntentsResponseSchema = z.strictObject({
    data: z.array(communicationsDocumentNotificationIntentSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=communications-document-notification.contract.js.map